'use client'

import { cn } from '@/lib/utils'

import {
  EASE,
  SectionHead,
  restoreDashes,
  useReveal,
} from '@/components/page-kit'
import { ACCOUNTS } from './data'

/* Four quadrants divided by hairlines, each with its authored `stat` set as a
   corner figure — "Close in 2 days", "Audit-ready always", "Matches in
   real-time", "Overspend prevented".

   A quadrant grid rather than the full-width rows the HRMS and Payroll pages
   use for their features, so the three read as siblings rather than copies.

   Heading is the first sentence of `description`, verbatim. */

const [firstSentence] = ACCOUNTS.description.split(/(?<=\.)\s+/)

export function AccountsFeatures() {
  const grid = useReveal<HTMLDivElement>(0.12)

  return (
    <section className="pt-16 sm:pt-20 md:pt-28">
      <div className="container mx-auto">
        <SectionHead eyebrow="Key features" title={firstSentence} />

        <div ref={grid.ref} className="mt-10 sm:mt-12">
          <div className="grid grid-cols-1 gap-px bg-border/70 sm:grid-cols-2">
            {ACCOUNTS.features.map((f, i) => (
              <article
                key={f.title}
                className="flex flex-col bg-background px-0 py-7 sm:px-7 sm:py-9"
                style={{
                  opacity: grid.shown ? 1 : 0,
                  transform: grid.shown ? 'none' : 'translateY(10px)',
                  transition: `opacity 500ms ${EASE} ${i * 90}ms, transform 500ms ${EASE} ${i * 90}ms`,
                }}
              >
                <div className="flex items-baseline gap-3">
                  <span className="text-[11px] font-semibold tabular-nums text-muted-foreground/60">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-heading text-lg font-normal leading-snug text-foreground sm:text-xl">
                    {f.title}
                  </h3>
                </div>

                <p className="mt-3 max-w-md pl-[26px] text-sm leading-relaxed text-muted-foreground sm:text-base">
                  {restoreDashes(f.description)}
                </p>

                {/* The corner figure, pinned to the bottom of the quadrant so
                    the four line up with each other. */}
                <p
                  className={cn(
                    'font-heading mt-auto pl-[26px] pt-6 text-base font-normal leading-tight',
                    'text-emerald-600 sm:text-lg dark:text-primary-active'
                  )}
                >
                  {f.stat}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
